/**
 * Commission Calculations
 *
 * Centralized utilities for calculating commissions with:
 * - Expense recoupment
 * - Agency splits
 * - Tier-based splits
 * - Quota progress
 * - Projections
 */

import type {
  AgentExpense,
  AgencyCommissionSettings,
  AgentCommissionSplit,
  AgentQuota,
  TierRule,
  CommissionBreakdown,
  CommissionSummary,
  CommissionProjection,
} from '../types/commission'
import type { Deal } from '../types'
import { parseDateOnly } from './formatters'
import { effectiveSplit, reconciledAmounts as sharedReconciledAmounts } from '../shared/commissionModel'

// ============ EXPENSE CALCULATIONS ============

/**
 * Get total unrerecouped expenses for an agent, optionally filtered by client
 */
export function getUnrecoupedExpenseTotal(
  expenses: AgentExpense[],
  agentId: string,
  clientId?: string
): number {
  return expenses
    .filter(e =>
      e.agent_id === agentId &&
      (e.status === 'approved' || e.status === 'partial') &&
      (!clientId || e.client_id === clientId)
    )
    .reduce((sum, e) => {
      const recouped = e.recouped_amount || 0
      return sum + (e.amount - recouped)
    }, 0)
}

/**
 * Get expenses that can be recouped from a specific deal
 */
export function getExpensesForRecoupment(
  expenses: AgentExpense[],
  agentId: string,
  clientId: string
): AgentExpense[] {
  return expenses.filter(e =>
    e.agent_id === agentId &&
    e.client_id === clientId &&
    (e.status === 'approved' || e.status === 'partial') &&
    (e.recouped_amount || 0) < e.amount
  )
}

/**
 * Calculate how much can be recouped from a commission payment
 */
export function calculateRecoupment(
  expenses: AgentExpense[],
  agentId: string,
  clientId: string,
  availableAmount: number
): { totalRecouped: number; recoupmentDetails: { expense_id: string; amount: number }[] } {
  const eligibleExpenses = getExpensesForRecoupment(expenses, agentId, clientId)
    .sort((a, b) => new Date(a.expense_date).getTime() - new Date(b.expense_date).getTime()) // Oldest first

  let remaining = availableAmount
  const recoupmentDetails: { expense_id: string; amount: number }[] = []

  for (const expense of eligibleExpenses) {
    if (remaining <= 0) break

    const outstanding = expense.amount - (expense.recouped_amount || 0)
    const toRecoup = Math.min(outstanding, remaining)

    if (toRecoup > 0) {
      recoupmentDetails.push({ expense_id: expense.id, amount: toRecoup })
      remaining -= toRecoup
    }
  }

  return {
    totalRecouped: availableAmount - remaining,
    recoupmentDetails,
  }
}

// ============ SPLIT CALCULATIONS ============

/**
 * Get the effective split for an agent on a given date
 * Checks for agent-specific overrides, tier rules, then falls back to agency defaults
 */
export function getEffectiveSplit(
  agencySettings: AgencyCommissionSettings | null,
  agentSplits: AgentCommissionSplit[],
  agentId: string,
  date: Date = new Date(),
  ytdRevenue?: number // For tier-based calculations
): { agentPercentage: number; agencyPercentage: number } | null {
  // Tier rules apply only when a YTD revenue figure is given; otherwise
  // the dated split, then the agency default, then none (shared rule).
  // The old 50/50 default was written into real ledger rows as if agreed.
  const dateStr = date.toISOString().split('T')[0]
  const agentSplit = agentSplits.find(s =>
    s.agent_id === agentId &&
    s.effective_date <= dateStr &&
    (!s.end_date || s.end_date >= dateStr)
  )
  if (agentSplit?.tier_rules && ytdRevenue !== undefined) {
    const tierSplit = getTierSplit(agentSplit.tier_rules, ytdRevenue)
    if (tierSplit) return tierSplit
  }
  if (!agentSplit && agencySettings?.tier_rules && ytdRevenue !== undefined) {
    const tierSplit = getTierSplit(agencySettings.tier_rules, ytdRevenue)
    if (tierSplit) return tierSplit
  }
  return effectiveSplit(agencySettings, agentSplits, agentId, date)
}

/**
 * Get split based on tier rules
 */
function getTierSplit(
  tierRules: TierRule[],
  revenue: number
): { agentPercentage: number; agencyPercentage: number } | null {
  // Sort tiers by min_revenue ascending
  const sortedTiers = [...tierRules].sort((a, b) => a.min_revenue - b.min_revenue)

  for (const tier of sortedTiers) {
    const inRange =
      revenue >= tier.min_revenue &&
      (tier.max_revenue === null || revenue <= tier.max_revenue)

    if (inRange) {
      return {
        agentPercentage: tier.agent_percentage,
        agencyPercentage: tier.agency_percentage,
      }
    }
  }

  return null
}

/**
 * Apply agency split to a net commission amount
 */
export function applySplit(
  netCommission: number,
  split: { agentPercentage: number; agencyPercentage: number }
): { agentTake: number; agencyTake: number } {
  const agentTake = (netCommission * split.agentPercentage) / 100
  const agencyTake = (netCommission * split.agencyPercentage) / 100
  return { agentTake, agencyTake }
}

// ============ FULL COMMISSION CALCULATION ============

/**
 * Calculate full commission breakdown for a deal
 */
export function calculateDealCommission(
  deal: Deal,
  expenses: AgentExpense[],
  agentId: string,
  split: { agentPercentage: number; agencyPercentage: number },
  recoupExpenses: boolean = true
): CommissionBreakdown {
  const dealValue = deal.value || 0
  const commissionRate = (deal as any).commission_rate || 0 // Default 3%
  const grossCommission = (dealValue * commissionRate) / 100

  // Get expenses for this client
  const clientId = deal.client_id || ''
  const clientExpenses = expenses.filter(e =>
    e.agent_id === agentId &&
    e.client_id === clientId
  )

  const totalExpenses = clientExpenses.reduce((sum, e) => sum + e.amount, 0)
  const unrecoupedExpenses = getUnrecoupedExpenseTotal(expenses, agentId, clientId)

  // Calculate net commission (gross - expenses to recoup)
  let netCommission = grossCommission
  if (recoupExpenses && unrecoupedExpenses > 0) {
    netCommission = Math.max(0, grossCommission - unrecoupedExpenses)
  }

  // Apply split
  const { agentTake, agencyTake } = applySplit(netCommission, split)

  return {
    deal_id: deal.id,
    deal_title: deal.title,
    client_name: (deal as any).expand?.client_id?.full_name || 'Unknown Client',
    deal_value: dealValue,
    commission_rate: commissionRate,
    gross_commission: grossCommission,
    expenses: clientExpenses,
    total_expenses: totalExpenses,
    unreccouped_expenses: unrecoupedExpenses,
    net_commission: netCommission,
    split,
    agent_take: agentTake,
    agency_take: agencyTake,
  }
}

// ============ QUOTA CALCULATIONS ============

/**
 * Calculate quota progress based on deals
 */
export function calculateQuotaProgress(
  quota: AgentQuota,
  deals: Deal[],
  contracts?: any[] // Contract type
): number {
  // Period bounds are calendar days; the end day is inclusive.
  const startDate = parseDateOnly(quota.period_start) ?? new Date(NaN)
  const endDate = parseDateOnly(quota.period_end) ?? new Date(NaN)
  endDate.setHours(23, 59, 59, 999)

  // Filter deals within the quota period
  const periodDeals = deals.filter(d => {
    const closeDate = parseDateOnly(d.actual_close_date)
    return closeDate && closeDate >= startDate && closeDate <= endDate &&
           d.stage === 'Executed'
  })

  switch (quota.quota_type) {
    case 'deal_count':
      return periodDeals.length

    case 'revenue':
      return periodDeals.reduce((sum, d) => sum + (d.value || 0), 0)

    case 'commission':
      return periodDeals.reduce((sum, d) => {
        const value = d.value || 0
        const rate = (d as any).commission_rate || 0
        return sum + (value * rate / 100)
      }, 0)

    case 'new_clients': {
      // Count unique clients from deals signed in the period
      const clientIds = new Set(periodDeals.map(d => d.client_id).filter(Boolean))
      return clientIds.size
    }

    case 'contract_value': {
      if (!contracts) return 0
      const periodContracts = contracts.filter(c => {
        const signDate = c.signing_date ? new Date(c.signing_date) : null
        return signDate && signDate >= startDate && signDate <= endDate
      })
      return periodContracts.reduce((sum, c) => sum + (c.total_value || 0), 0)
    }

    default:
      return 0
  }
}

/**
 * Get quota completion percentage
 */
export function getQuotaPercentage(current: number, target: number): number {
  if (target <= 0) return 0
  return Math.min(100, (current / target) * 100)
}

/**
 * Determine quota status based on progress and dates
 */
export function determineQuotaStatus(
  quota: AgentQuota,
  currentProgress: number
): 'active' | 'achieved' | 'missed' {
  const now = new Date()
  const endDate = parseDateOnly(quota.period_end) ?? new Date(NaN)
  endDate.setHours(23, 59, 59, 999)

  if (currentProgress >= quota.target_amount) {
    return 'achieved'
  }

  if (now > endDate) {
    return 'missed'
  }

  return 'active'
}

// ============ SUMMARY & PROJECTIONS ============

/**
 * Calculate commission summary for a period
 */
export function calculateCommissionSummary(
  deals: Deal[],
  expenses: AgentExpense[],
  quotas: AgentQuota[],
  agentId: string,
  split: { agentPercentage: number; agencyPercentage: number },
  startDate: Date,
  endDate: Date
): CommissionSummary {
  // Filter deals in the period that are executed
  const executedDeals = deals.filter(d => {
    const closeDate = parseDateOnly(d.actual_close_date)
    return closeDate && closeDate >= startDate && closeDate <= endDate &&
           d.stage === 'Executed'
  })

  // Calculate gross commission
  const grossEarned = executedDeals.reduce((sum, d) => {
    const value = d.value || 0
    const rate = (d as any).commission_rate || 0
    return sum + (value * rate / 100)
  }, 0)

  // Calculate expenses
  const periodExpenses = expenses.filter(e =>
    e.agent_id === agentId &&
    new Date(e.expense_date) >= startDate &&
    new Date(e.expense_date) <= endDate
  )

  const expensesAdvanced = periodExpenses.reduce((sum, e) => sum + e.amount, 0)
  const expensesRecouped = periodExpenses
    .filter(e => e.status === 'recouped')
    .reduce((sum, e) => sum + (e.recouped_amount || e.amount), 0)

  // Calculate net commission
  const netCommission = Math.max(0, grossEarned - getUnrecoupedExpenseTotal(expenses, agentId))

  // Apply split
  const { agentTake, agencyTake } = applySplit(netCommission, split)

  // Get pending deals
  const pendingDeals = deals.filter(d =>
    d.stage !== 'Executed' && d.stage !== 'Lost'
  )
  const pendingValue = pendingDeals.reduce((sum, d) => sum + (d.value || 0), 0)

  // Get quota progress
  const quotaProgress = quotas.map(q => ({
    quota_id: q.id,
    quota_type: q.quota_type,
    target: q.target_amount,
    current: q.current_progress || 0,
    percentage: getQuotaPercentage(q.current_progress || 0, q.target_amount),
    status: q.status,
  }))

  // Format period string
  const periodStr = `${startDate.toISOString().split('T')[0]} to ${endDate.toISOString().split('T')[0]}`

  return {
    period: periodStr,
    gross_earned: grossEarned,
    expenses_advanced: expensesAdvanced,
    expenses_recouped: expensesRecouped,
    net_commission: netCommission,
    agent_take: agentTake,
    agency_take: agencyTake,
    pending_deals: pendingDeals.length,
    pending_value: pendingValue,
    quota_progress: quotaProgress,
  }
}

/**
 * Project future commission earnings based on pipeline
 */
export function projectCommissionEarnings(
  pendingDeals: Deal[],
  split: { agentPercentage: number; agencyPercentage: number },
  months: number = 6
): CommissionProjection[] {
  const projections: CommissionProjection[] = []
  const now = new Date()

  for (let i = 0; i < months; i++) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() + i, 1)
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + i + 1, 0)
    const monthStr = monthDate.toISOString().slice(0, 7) // YYYY-MM

    // Find deals expected to close in this month
    const monthDeals = pendingDeals.filter(d => {
      const expectedClose = d.expected_close ? new Date(d.expected_close) : null
      return expectedClose && expectedClose >= monthDate && expectedClose <= monthEnd
    })

    // Calculate projections (probability-weighted)
    let projectedGross = 0
    for (const deal of monthDeals) {
      const value = deal.value || 0
      const rate = (deal as any).commission_rate || 0
      const probability = (deal.probability || 50) / 100
      const grossCommission = (value * rate / 100)
      projectedGross += grossCommission * probability
    }

    const projectedNet = projectedGross // Simplified - could factor in avg expense rate
    const { agentTake } = applySplit(projectedNet, split)

    projections.push({
      month: monthStr,
      projected_gross: projectedGross,
      projected_net: projectedNet,
      projected_agent_take: agentTake,
      deal_count: monthDeals.length,
      probability_weighted: true,
    })
  }

  return projections
}

// ============ UTILITY FUNCTIONS ============

/**
 * Format currency for display
 */
export function formatCommission(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

/**
 * Get period boundaries for a quota period type
 */
export function getQuotaPeriodBounds(
  periodType: 'monthly' | 'quarterly' | 'yearly',
  referenceDate: Date = new Date()
): { start: Date; end: Date } {
  const year = referenceDate.getFullYear()
  const month = referenceDate.getMonth()
  const quarter = Math.floor(month / 3)

  switch (periodType) {
    case 'monthly':
      return {
        start: new Date(year, month, 1),
        end: new Date(year, month + 1, 0),
      }

    case 'quarterly':
      return {
        start: new Date(year, quarter * 3, 1),
        end: new Date(year, (quarter + 1) * 3, 0),
      }

    case 'yearly':
      return {
        start: new Date(year, 0, 1),
        end: new Date(year, 11, 31),
      }
  }
}

/**
 * A ledger entry re-priced to a new gross commission. Expenses already
 * recouped against it stay recouped, but never exceed the new gross; the
 * split keeps the percentage recorded on the entry. Pure, so it can be
 * asserted without a database.
 */
export function reconciledAmounts(
  entry: { expenses_deducted: number | null; split_percentage: number | null },
  gross: number,
): { gross_amount: number; expenses_deducted: number; net_amount: number; agent_share: number; agency_share: number } {
  return sharedReconciledAmounts(entry, gross)
}

/** Two money amounts are the same to the cent. */
export function sameMoney(a: number | null | undefined, b: number | null | undefined): boolean {
  return Math.abs((a ?? 0) - (b ?? 0)) < 0.005
}
